import { clamp } from 'lodash';

const EVENT_HISTORY_COUNT = 6;
const MIN_EVENT_HISTORY_COUNT = 3;
const NEW_EVENT_TIME_DIFFERENCE_THRESHOLD = 300;
// 60fps
const STANDARD_ANIMATION_FRAME = 1000 / 60;
const NORMALISED_DELTA_MIN_TIME_DIFFERENCE = 8;
const NORMALISED_DELTA_MAX_TIME_DIFFERENCE = 80;
const MIN_DIRECTION_DELTA = 0.5;
const INERTIA_TAIL_MAGNITUDE_THRESHOLD = 1.5;
const LOW_ENERGY_INERTIA_MAGNITUDE_THRESHOLD = 6;
const MAX_ALLOWED_INCREASE_RATIO = 1.6;
const MAX_ALLOWED_INCREASE_COUNT = 1;
const INERTIA_ENVELOPE_GROWTH_TOLERANCE_RATIO = 1.25;
const INERTIA_DECAY_STEP_RATIO = 0.95;
const MIN_INERTIA_DECAY_STEP_COUNT = 1;

enum WheelEventSummaryPropName {
    deltaY = 'deltaY',
    deltaX = 'deltaX',
    weightedDeltaY = 'weightedDeltaY',
    weightedDeltaX = 'weightedDeltaX',
}

interface InertiaScrollDetector {
    addWheelEvent: (event: WheelEvent, options?: { isTrackpadPinchStyleWheel?: boolean }) => void;
    isInertiaScroll: () => boolean;
    /** Clears rolling history, e.g. when switching boards so stale gestures do not affect classification. */
    reset: () => void;
}

interface WheelEventSummary {
    timeStamp: number;
    deltaX: number;
    deltaY: number;
    // In order to fairly compare two events we need to get the weighted value
    //  in case an animation frame has been skipped
    weightedDeltaX: number;
    weightedDeltaY: number;
    timeDifference: number;
    /** macOS trackpad pinch is delivered as wheel + ctrlKey without metaKey; do not treat its small deltas as scroll inertia. */
    isTrackpadPinchStyleWheel: boolean;
}

/**
 * Converts a raw wheel delta into a frame-normalised magnitude so events from different
 * dispatch intervals remain comparable.
 */
const getNormalisedDelta = (delta: number, timeDifference: number): number => {
    const clampedTimeDifference = clamp(
        timeDifference,
        NORMALISED_DELTA_MIN_TIME_DIFFERENCE,
        NORMALISED_DELTA_MAX_TIME_DIFFERENCE,
    );

    return delta * (STANDARD_ANIMATION_FRAME / clampedTimeDifference);
};

/**
 * Converts a delta into a coarse direction (-1, 0, 1), treating tiny movement as no direction.
 */
const getDirection = (delta: number): number => {
    if (Math.abs(delta) < MIN_DIRECTION_DELTA) return 0;
    return Math.sign(delta);
};

/**
 * Classifies inertia for one scroll axis using recent direction-consistent, normalised events.
 */
const isInertiaScrollInDirection = (
    events: Array<WheelEventSummary>,
    propName: WheelEventSummaryPropName,
    weightedPropName: WheelEventSummaryPropName,
) => {
    const directionEvents = events.filter((event) => getDirection(event[propName]) !== 0).slice(-EVENT_HISTORY_COUNT);

    if (directionEvents.length < MIN_EVENT_HISTORY_COUNT) return false;

    const mostRecentEvent = directionEvents[directionEvents.length - 1];
    const previousEvent = directionEvents[directionEvents.length - 2];

    // If there's no delta in this direction, then it's not an inertia scroll in this direction
    if (mostRecentEvent[propName] === 0) return false;

    // If there's a chang of direction, it's a manual scroll
    if (mostRecentEvent[propName] * previousEvent[propName] < 0) return false;

    const mostRecentDirection = getDirection(mostRecentEvent[propName]);
    const hasDirectionChange = directionEvents.some((event) => getDirection(event[propName]) !== mostRecentDirection);
    if (hasDirectionChange) return false;

    const weightedMagnitudes = directionEvents.map((event) => Math.abs(event[weightedPropName]));
    const weightedRatios = weightedMagnitudes.slice(1).map((magnitude, index) => {
        const previousMagnitude = weightedMagnitudes[index];

        if (!previousMagnitude) return 1;
        return magnitude / previousMagnitude;
    });

    const significantIncreaseCount = weightedRatios.filter((ratio) => ratio > MAX_ALLOWED_INCREASE_RATIO).length;
    if (significantIncreaseCount > MAX_ALLOWED_INCREASE_COUNT) return false;

    const oldestMagnitude = weightedMagnitudes[0];
    const latestMagnitude = weightedMagnitudes[weightedMagnitudes.length - 1];

    // Tail-end momentum often becomes noisy at low magnitudes due to event batching and floating-point
    // noise. Treat this regime as inertia to avoid accidental mode transitions around 100%.
    // Trackpad pinch uses the same small per-frame magnitudes while the user is still driving zoom;
    // only apply this shortcut when the latest event is not a pinch-style ctrlKey wheel.
    if (latestMagnitude <= LOW_ENERGY_INERTIA_MAGNITUDE_THRESHOLD && !mostRecentEvent.isTrackpadPinchStyleWheel) {
        return true;
    }

    const isStableOrDecayingEnvelope = latestMagnitude <= oldestMagnitude * INERTIA_ENVELOPE_GROWTH_TOLERANCE_RATIO;
    const decayStepCount = weightedRatios.filter((ratio) => ratio < INERTIA_DECAY_STEP_RATIO).length;

    if (
        !isStableOrDecayingEnvelope &&
        decayStepCount < MIN_INERTIA_DECAY_STEP_COUNT &&
        latestMagnitude > INERTIA_TAIL_MAGNITUDE_THRESHOLD
    ) {
        return false;
    }

    return true;
};

/**
 * The inertia scroll detector can be used to detect when a wheel event is most likely a result
 * of Apple's Magic Mouse inertia scroll, rather than a manual scroll from the user.
 *
 * Process:
 * 1. Create the inertia scroll detector:
 *    ```const inertiaScrollDetector = createInertiaScrollDetector();```
 * 2. On a wheel event add the event into the inertia scroll detector:
 *    ```inertiaScrollDetector.addWheelEvent(wheelEvent);```
 * 3. When desired, check to see if the previously added event is an inertia scroll event:
 *    ```inertiaScrollDetector.isInertiaScroll();```
 * 4. Optionally clear history (e.g. after navigation): ```inertiaScrollDetector.reset();```
 *
 * Technique:
 * We use a variety of heuristics to estimate whether an event is from inertia scroll or manual scroll
 * (as this is not made public in the event).
 * It's a manual scroll:
 *  - If the time since the last event is larger than the continuity threshold
 *  - If there's a change in direction of the scroll
 *  - If there are repeated significant increases in normalised event magnitude
 *  - If the recent events do not form an overall decaying envelope
 */
export const createInertiaScrollDetector = (): InertiaScrollDetector => {
    let events: Array<WheelEventSummary> = [];

    /**
     * Stores a wheel event snapshot and updates the rolling event history used by detection.
     */
    const addWheelEvent = (event: WheelEvent, options?: { isTrackpadPinchStyleWheel?: boolean }) => {
        const previousEvent = events[events.length - 1];

        // Large pauses indicate a new gesture. Reset the history so stale values do not affect
        // the next classification.
        if (previousEvent && event.timeStamp - previousEvent.timeStamp > NEW_EVENT_TIME_DIFFERENCE_THRESHOLD) {
            events = [];
        }

        const newEvent: WheelEventSummary = {
            timeStamp: event.timeStamp,
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            weightedDeltaX: event.deltaX,
            weightedDeltaY: event.deltaY,
            timeDifference: 0,
            isTrackpadPinchStyleWheel: !!options?.isTrackpadPinchStyleWheel,
        };

        // If we have a previous event then we can perform weighting of the deltaY
        if (events.length > 0) {
            const prevEvent = events[events.length - 1];

            newEvent.timeDifference = event.timeStamp - prevEvent.timeStamp;

            // Always normalise by event spacing so dropped frames (coalesced wheel deltas)
            // compare more like-for-like with regular frame cadence.
            newEvent.weightedDeltaY = getNormalisedDelta(event.deltaY, newEvent.timeDifference);
            newEvent.weightedDeltaX = getNormalisedDelta(event.deltaX, newEvent.timeDifference);
        }

        events.push(newEvent);
        events = events.slice(-EVENT_HISTORY_COUNT);
    };

    /**
     * Returns true when the latest wheel input most closely matches an inertia tail.
     */
    const isInertiaScroll = (): boolean => {
        // If we don't have enough events, assume it's not an inertia scroll
        if (events.length < MIN_EVENT_HISTORY_COUNT) return false;

        const mostRecentEvent = events[events.length - 1];

        // If the delay is larger than the continuity threshold, it must be a manual scroll
        if (mostRecentEvent.timeDifference > NEW_EVENT_TIME_DIFFERENCE_THRESHOLD) return false;

        return (
            isInertiaScrollInDirection(
                events,
                WheelEventSummaryPropName.deltaY,
                WheelEventSummaryPropName.weightedDeltaY,
            ) ||
            isInertiaScrollInDirection(
                events,
                WheelEventSummaryPropName.deltaX,
                WheelEventSummaryPropName.weightedDeltaX,
            )
        );
    };

    const reset = (): void => {
        events = [];
    };

    return {
        addWheelEvent,
        isInertiaScroll,
        reset,
    };
};
